// ============================================
// Teach Mode — Learn from Human Demonstration
// The user plays the nurse. An AI patient talks
// to them. When done, the system extracts what
// the user did well into prompt rules.
// /teach
// ============================================

import { useState, useRef, useEffect } from 'react';
import {
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Square,
  ClipboardList,
} from 'lucide-react';
import { claudeChat } from '../../services/ai/providers/claude';
import {
  buildTwinPrompt,
  deriveBehaviorFromPersona,
  getDefaultPromptRules,
} from '../../services/patientJourney/twinEngine';
import { extractTeachingInsights, applyPromptSuggestions } from '../../services/patientJourney/promptOptimizer';
import { getActivePromptConfig, savePromptConfig } from '../../services/firestore/promptService';
import type { Message } from '../../types/patientJourney';
import type { NursePromptConfig, OptimizerChatMessage } from '../../types/promptConfig';
import {
  PERSONA_OPTIONS,
  PERSONA_MAP,
  buildDefaultTouchpoint,
} from '../../data/personas/customerFunnelPersonas';
import { T, F, modules } from '../../components/quality/tokens';
import { ModuleShell } from '../../components/quality/ModuleShell';
import { ModuleHeader } from '../../components/quality/ModuleHeader';
import { ChatBubble } from '../../components/quality/ChatBubble';
import { Pill } from '../../components/quality/Pill';

// ── Constants ──

const PATIENT_MODEL = 'claude-sonnet-4-5-20250929';
const GREEN = modules.teach.color;
const GREEN_SOFT = 'rgba(22,163,74,0.08)';
const GREEN_BORDER = 'rgba(22,163,74,0.25)';
const AMBER = '#D97706';
const AMBER_SOFT = 'rgba(217,119,6,0.08)';

// ── Types ──

type Phase = 'setup' | 'teaching' | 'extracting' | 'insights';

// ── Component ──

export function TeachMode() {
  const [selectedPersona, setSelectedPersona] = useState('clean_gate1');
  const [phase, setPhase] = useState<Phase>('setup');
  const [conversationLog, setConversationLog] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isPatientTyping, setIsPatientTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Claude patient state
  const patientHistoryRef = useRef<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const patientSystemPromptRef = useRef<string>('');

  // Teaching extractor
  const [activePromptConfig, setActivePromptConfig] = useState<NursePromptConfig | null>(null);
  const [extractorChat, setExtractorChat] = useState<OptimizerChatMessage[]>([]);
  const [isChatting, setIsChatting] = useState(false);
  const [extractorInput, setExtractorInput] = useState('');
  const [showExtractor, setShowExtractor] = useState(true);
  const [showLabBrief, setShowLabBrief] = useState(false);

  // Refs
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    getActivePromptConfig().then((c) => { if (c) setActivePromptConfig(c); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (phase !== 'teaching' && phase !== 'extracting') return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationLog.length, isPatientTyping]);

  const persona = PERSONA_MAP[selectedPersona];

  async function handleStart() {
    if (!persona) return;
    setError(null);
    setConversationLog([]);
    setExtractorChat([]);
    setShowExtractor(true);
    setPhase('teaching');
    setIsPatientTyping(true);

    const touchpoint = buildDefaultTouchpoint(persona.personaId);
    const behavior = deriveBehaviorFromPersona(persona);
    const basePrompt = buildTwinPrompt(persona, behavior, touchpoint);

    // Add teaching-specific rules to the patient prompt
    const teachingRules = `

ADDITIONAL RULES FOR TEACHING SESSION:
- You are talking to a HUMAN nurse, not an AI. They are the product owner demonstrating ideal nurse behavior.
- Be a realistic, natural patient. React authentically to good empathy and push back on poor communication.
- If the human nurse says something that makes you feel heard, show it — open up more, share more details.
- If the human nurse is dismissive or too clinical too fast, pull back — give shorter answers, seem less engaged.
- Your emotional reactions should feel real and proportional.
- Do NOT end the conversation on your own. Let the human decide when they're done.
- Start by describing why you're here today — what brought you in, how you're feeling.`;

    patientSystemPromptRef.current = basePrompt + teachingRules;
    patientHistoryRef.current = [];

    try {
      // Get the AI patient's opening line
      const openingInstruction = `You are the patient. This is your first message to the nurse. Describe why you're here today — mention your main symptoms and how they've been affecting your life. Be natural and conversational, like a real patient walking into a clinic. 2-4 sentences.`;

      const patientOpening = await claudeChat(
        PATIENT_MODEL,
        patientSystemPromptRef.current,
        [],
        openingInstruction,
        0.7,
        512,
      );

      patientHistoryRef.current.push(
        { role: 'user', content: openingInstruction },
        { role: 'assistant', content: patientOpening },
      );

      setConversationLog([{
        role: 'patient',
        content: patientOpening,
        timestamp: new Date().toISOString(),
        touchpointId: touchpoint.touchpointId,
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start');
      setPhase('setup');
    } finally {
      setIsPatientTyping(false);
    }
  }

  async function handleSend() {
    const text = chatInput.trim();
    if (!text || isPatientTyping || phase !== 'teaching') return;
    setChatInput('');

    const touchpoint = buildDefaultTouchpoint(persona?.personaId || '');

    // User's message goes as the NURSE (right-aligned gold bubbles)
    setConversationLog((prev) => [...prev, {
      role: 'ai_nurse',
      content: text,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint.touchpointId,
    }]);
    setIsPatientTyping(true);

    try {
      // Send nurse message to Claude patient — nurse text becomes 'user' input to Claude
      const resp = await claudeChat(
        PATIENT_MODEL,
        patientSystemPromptRef.current,
        patientHistoryRef.current,
        `[The nurse says]: ${text}`,
        0.7,
        512,
      );

      patientHistoryRef.current.push(
        { role: 'user', content: `[The nurse says]: ${text}` },
        { role: 'assistant', content: resp },
      );

      setConversationLog((prev) => [...prev, {
        role: 'patient',
        content: resp,
        timestamp: new Date().toISOString(),
        touchpointId: touchpoint.touchpointId,
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Patient AI error');
    } finally {
      setIsPatientTyping(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }

  async function handleEndAndLearn() {
    if (conversationLog.length < 4) {
      setError('Have at least 2 exchanges before extracting insights.');
      return;
    }
    setPhase('extracting');
    setError(null);
    setIsChatting(true);

    try {
      const currentRules = activePromptConfig?.template || getDefaultPromptRules();
      const personaContext = persona
        ? `${persona.demographics.age}yo ${persona.demographics.sex}, ${persona.conditions.join(', ')}. ${persona.medications.length > 0 ? 'Meds: ' + persona.medications.map((m) => m.name).join(', ') : 'No medications.'} Labs: ${persona.labs.filter((l) => l.trigger).map((l) => `${l.biomarker} ${l.value} ${l.unit} (${l.status})`).join(', ')}.`
        : '';

      const { reply, suggestions } = await extractTeachingInsights(
        [],
        'Analyze this teaching demonstration. What did the human nurse do well? What patterns should we capture in the prompt?',
        {
          conversationLog,
          currentPromptRules: currentRules,
          personaName: persona?.name || 'Unknown',
          personaContext,
        },
      );

      setExtractorChat([{
        role: 'optimizer',
        content: reply,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        timestamp: new Date().toISOString(),
      }]);
      setShowExtractor(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Teaching extraction failed');
    } finally {
      setIsChatting(false);
      setPhase('insights');
    }
  }

  function handleReset() {
    setPhase('setup');
    setConversationLog([]);
    setChatInput('');
    setError(null);
    setExtractorChat([]);
    setShowExtractor(true);
    patientHistoryRef.current = [];
    patientSystemPromptRef.current = '';
  }

  // ── Extractor handlers (same pattern as NurseTest optimizer) ──

  function acceptSuggestion(msgIdx: number, sugId: string) {
    setExtractorChat((prev) =>
      prev.map((msg, i) => i === msgIdx && msg.suggestions
        ? { ...msg, suggestions: msg.suggestions.map((s) => s.id === sugId ? { ...s, status: 'accepted' as const } : s) }
        : msg),
    );
  }

  function rejectSuggestion(msgIdx: number, sugId: string) {
    setExtractorChat((prev) =>
      prev.map((msg, i) => i === msgIdx && msg.suggestions
        ? { ...msg, suggestions: msg.suggestions.map((s) => s.id === sugId ? { ...s, status: 'rejected' as const } : s) }
        : msg),
    );
  }

  async function handleSendExtractor() {
    const text = extractorInput.trim();
    if (!text || isChatting) return;
    setExtractorChat((prev) => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
    setExtractorInput('');
    setIsChatting(true);

    try {
      const currentRules = activePromptConfig?.template || getDefaultPromptRules();
      const personaContext = persona
        ? `${persona.demographics.age}yo ${persona.demographics.sex}, ${persona.conditions.join(', ')}.`
        : '';

      const history = extractorChat.map((m) => ({
        role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.content,
      }));

      const { reply, suggestions } = await extractTeachingInsights(history, text, {
        conversationLog,
        currentPromptRules: currentRules,
        personaName: persona?.name || 'Unknown',
        personaContext,
      });

      setExtractorChat((prev) => [...prev, {
        role: 'optimizer',
        content: reply,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        timestamp: new Date().toISOString(),
      }]);
    } catch (err) {
      setExtractorChat((prev) => [...prev, {
        role: 'optimizer',
        content: `Error: ${err instanceof Error ? err.message : 'unknown'}`,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsChatting(false);
    }
  }

  async function handleApply() {
    const accepted = extractorChat.flatMap((m) => (m.suggestions || []).filter((s) => s.status === 'accepted'));
    if (accepted.length === 0) return;
    const currentTemplate = activePromptConfig?.template || getDefaultPromptRules();
    const newTemplate = applyPromptSuggestions(currentTemplate, accepted);
    const newVersion = (activePromptConfig?.version || 0) + 1;
    const newConfig: NursePromptConfig = {
      versionId: `prompt_v${newVersion}_${Date.now()}`,
      version: newVersion,
      isActive: true,
      createdAt: new Date().toISOString(),
      source: 'teaching_session',
      template: newTemplate,
      changelog: `Teaching session with ${persona?.name || 'unknown'}: ${accepted.map((s) => s.reasoning).join('; ')}`,
      stats: { runsCount: 0, avgPassRate: 0, bestPassRate: 0, worstPassRate: 100 },
    };
    try {
      await savePromptConfig(newConfig);
      setActivePromptConfig(newConfig);
      handleReset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  const allSuggestions = extractorChat.flatMap((m) => m.suggestions || []);
  const acceptedCount = allSuggestions.filter((s) => s.status === 'accepted').length;
  const pendingCount = allSuggestions.filter((s) => s.status === 'pending').length;
  const selectedOption = PERSONA_OPTIONS.find((p) => p.value === selectedPersona);

  // ── Render ──

  return (
    <ModuleShell>
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
        background: T.bg, fontFamily: F.body,
      }}>
        {/* ── Header ── */}
        <ModuleHeader module="teach" subtitle={
          phase === 'setup' ? 'Capture Expertise \u2192 Prompt Rules'
            : phase === 'teaching' ? `${elapsed}s \u2014 You are the nurse`
            : phase === 'extracting' ? 'Extracting insights...'
            : 'Insights ready'
        } />

        {/* Reset button in insights phase */}
        {phase === 'insights' && (
          <div style={{ position: 'absolute', top: 18, right: 60, zIndex: 10 }}>
            <button
              onClick={handleReset}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 8, borderRadius: T.radius.sm, color: T.textMuted,
              }}
            >
              <RotateCcw size={18} />
            </button>
          </div>
        )}

        {/* ── Setup Screen ── */}
        {phase === 'setup' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px' }}>
            <div style={{ maxWidth: 480, margin: '0 auto' }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h2 style={{
                  fontSize: T.fontSize.xl, fontWeight: 600,
                  color: T.text, margin: 0,
                }}>
                  Teach the Nurse
                </h2>
                <p style={{
                  fontSize: T.fontSize.sm, color: T.textMuted,
                  marginTop: 4, marginBottom: 0,
                }}>
                  Pick a patient persona. You play the nurse. Show the AI how it&apos;s done.
                </p>
                <p style={{
                  fontSize: T.fontSize.xs, color: GREEN,
                  opacity: 0.8, marginTop: 8, marginBottom: 0,
                }}>
                  The system watches what you do and extracts it into prompt rules.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PERSONA_OPTIONS.map((opt) => {
                  const p = PERSONA_MAP[opt.value];
                  const isSelected = selectedPersona === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedPersona(opt.value)}
                      style={{
                        width: '100%', textAlign: 'left' as const,
                        padding: '12px 16px',
                        borderRadius: T.radius.xl,
                        border: `1.5px solid ${isSelected ? GREEN_BORDER : T.border}`,
                        background: isSelected ? GREEN_SOFT : T.bgCard,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div>
                          <p style={{
                            fontSize: T.fontSize.base, fontWeight: 500, margin: 0,
                            color: isSelected ? GREEN : T.text,
                          }}>
                            {opt.label}
                          </p>
                          <p style={{
                            fontSize: T.fontSize.xs, color: T.textMuted, margin: 0,
                          }}>
                            {opt.sub}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {p && (
                            <span style={{ fontSize: T.fontSize.xs, color: T.textMuted }}>
                              {p.labs.filter((l) => l.trigger).length} markers
                            </span>
                          )}
                          {isSelected && (
                            <div style={{
                              width: 20, height: 20, borderRadius: T.radius.full,
                              background: GREEN, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <CheckCircle2 size={12} style={{ color: '#fff' }} />
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && p && (
                        <div style={{
                          marginTop: 8, paddingTop: 8,
                          borderTop: `1px solid rgba(22,163,74,0.2)`,
                        }}>
                          <p style={{
                            fontSize: T.fontSize.xs, color: T.textSoft, margin: 0,
                          }}>
                            {p.conditions.join(' / ')}
                          </p>
                          {p.medications.length > 0 && (
                            <p style={{
                              fontSize: T.fontSize.xs, color: AMBER,
                              marginTop: 2, marginBottom: 0,
                            }}>
                              Meds: {p.medications.map((m) => m.name).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleStart}
                style={{
                  width: '100%', padding: '16px 0',
                  borderRadius: T.radius.xl,
                  background: GREEN, color: '#fff',
                  fontWeight: 700, fontSize: T.fontSize.lg,
                  border: 'none', cursor: 'pointer',
                  marginTop: 24,
                  transition: 'opacity 0.15s',
                }}
              >
                Start Teaching
              </button>
            </div>
          </div>
        )}

        {/* ── Chat View ── */}
        {(phase === 'teaching' || phase === 'extracting' || phase === 'insights') && (
          <>
            {/* Green role banner */}
            <div style={{
              flexShrink: 0, padding: '10px 16px',
              background: GREEN, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setShowLabBrief(!showLabBrief)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 8px', borderRadius: T.radius.sm,
                    fontSize: T.fontSize.xs, fontWeight: 600,
                    cursor: 'pointer', border: '1px solid rgba(255,255,255,0.3)',
                    background: showLabBrief ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                  }}
                >
                  <ClipboardList size={12} />
                  Labs
                </button>
                <span style={{ fontSize: T.fontSize.sm, fontWeight: 500 }}>
                  You are the nurse. AI is playing {selectedOption?.label}.
                </span>
              </div>
              {phase === 'teaching' && (
                <button
                  onClick={handleEndAndLearn}
                  disabled={isPatientTyping || conversationLog.length < 4}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: T.radius.sm,
                    background: 'rgba(255,255,255,0.2)', color: '#fff',
                    fontSize: T.fontSize.xs, fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.3)',
                    cursor: isPatientTyping || conversationLog.length < 4 ? 'not-allowed' : 'pointer',
                    opacity: isPatientTyping || conversationLog.length < 4 ? 0.4 : 1,
                  }}
                >
                  <Square size={10} />
                  End & Learn
                </button>
              )}
            </div>

            {/* Lab Brief — the chart the nurse has open */}
            {showLabBrief && persona && (
              <div style={{
                flexShrink: 0, maxHeight: '40vh', overflowY: 'auto',
                background: T.bgCard, borderBottom: `1px solid ${T.border}`,
                padding: '12px 16px',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 8,
                }}>
                  <p style={{
                    fontSize: T.fontSize.xs, fontWeight: 600,
                    color: AMBER, margin: 0,
                  }}>
                    Patient Chart — {persona.demographics.name}, {persona.demographics.age}{persona.demographics.sex === 'Female' ? 'F' : 'M'}
                  </p>
                  <button
                    onClick={() => setShowLabBrief(false)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 4, color: T.textMuted,
                    }}
                  >
                    <ChevronUp size={14} />
                  </button>
                </div>
                {persona.conditions.length > 0 && (
                  <p style={{
                    fontSize: T.fontSize.xs, color: T.textSoft, margin: '0 0 8px 0',
                  }}>
                    <span style={{ color: T.textMuted }}>Presenting:</span> {persona.conditions.join(', ')}
                  </p>
                )}
                {persona.medications.length > 0 && (
                  <p style={{
                    fontSize: T.fontSize.xs, color: AMBER, margin: '0 0 8px 0',
                  }}>
                    <span style={{ color: T.textMuted }}>Meds:</span> {persona.medications.map((m) => `${m.name} ${m.dose}`).join(', ')}
                  </p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {persona.labs.map((lab) => {
                    const isFlag = lab.status !== 'optimal';
                    return (
                      <div key={lab.biomarker} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderRadius: T.radius.sm,
                        fontSize: T.fontSize.xs,
                        background: isFlag ? AMBER_SOFT : T.bgSoft,
                        border: `1px solid ${isFlag ? 'rgba(217,119,6,0.15)' : T.border}`,
                      }}>
                        <span style={{
                          fontWeight: 500, color: isFlag ? AMBER : T.text,
                        }}>
                          {lab.biomarker}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: T.text, fontWeight: 600 }}>
                            {lab.value} {lab.unit}
                          </span>
                          <span style={{ color: T.textMuted }}>
                            Std: {lab.conventionalRange}
                          </span>
                          <span style={{
                            fontWeight: 600, color: isFlag ? AMBER : GREEN,
                          }}>
                            Fn: {lab.functionalRange}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Extracting progress */}
            {phase === 'extracting' && (
              <div style={{
                flexShrink: 0, padding: '8px 16px',
                background: GREEN_SOFT,
                borderBottom: `1px solid rgba(22,163,74,0.2)`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Loader2 size={14} style={{ color: GREEN, flexShrink: 0, animation: 'spin 1s linear infinite' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      background: T.border, borderRadius: T.radius.full, height: 6,
                    }}>
                      <div style={{
                        height: 6, borderRadius: T.radius.full,
                        background: GREEN, width: '60%',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  </div>
                  <span style={{
                    fontSize: T.fontSize.xs, color: GREEN,
                    fontWeight: 500, flexShrink: 0,
                  }}>
                    Extracting patterns...
                  </span>
                </div>
              </div>
            )}

            {/* Messages — ROLES SWAPPED: patient on left (gray bg), nurse/user on right (green) */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '16px 16px',
            }}>
              {conversationLog.map((msg, idx) => {
                const isNurse = msg.role === 'ai_nurse'; // This is the HUMAN user
                return (
                  <ChatBubble
                    key={idx}
                    text={msg.content}
                    isNurse={isNurse}
                    themeColor={GREEN}
                    label={isNurse ? 'You (Nurse)' : selectedOption?.label}
                  />
                );
              })}

              {isPatientTyping && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <div style={{
                    background: T.bgInput, border: `1px solid ${T.border}`,
                    borderRadius: '14px 14px 4px 14px',
                    padding: '10px 14px',
                    boxShadow: T.shadowSm,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: T.radius.full,
                          background: 'rgba(217,119,6,0.6)', animation: 'bounce 1s infinite',
                          animationDelay: '0ms',
                        }} />
                        <div style={{
                          width: 8, height: 8, borderRadius: T.radius.full,
                          background: 'rgba(217,119,6,0.6)', animation: 'bounce 1s infinite',
                          animationDelay: '150ms',
                        }} />
                        <div style={{
                          width: 8, height: 8, borderRadius: T.radius.full,
                          background: 'rgba(217,119,6,0.6)', animation: 'bounce 1s infinite',
                          animationDelay: '300ms',
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Insights panel (after extraction) */}
            {phase === 'insights' && extractorChat.length > 0 && (
              <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
                <button
                  onClick={() => setShowExtractor(!showExtractor)}
                  style={{
                    width: '100%', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: T.bgSoft, border: 'none', cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pill text="EXTRACTED INSIGHT" color={GREEN} bg={GREEN_SOFT} />
                    <span style={{
                      fontSize: T.fontSize.sm, fontWeight: 600, color: T.text,
                    }}>
                      Teaching Insights
                    </span>
                  </div>
                  {showExtractor
                    ? <ChevronDown size={16} style={{ color: T.textMuted }} />
                    : <ChevronUp size={16} style={{ color: T.textMuted }} />}
                </button>

                {showExtractor && (
                  <div style={{
                    maxHeight: '50vh', overflowY: 'auto',
                    padding: '12px 16px', background: T.bgCard,
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {extractorChat.map((msg, msgIdx) => (
                        <div key={msgIdx}>
                          <div style={{
                            borderRadius: T.radius.lg, padding: '10px 12px',
                            background: msg.role === 'user' ? T.bgSoft : GREEN_SOFT,
                            border: `1px solid ${msg.role === 'user' ? T.border : GREEN_BORDER}`,
                            marginLeft: msg.role === 'user' ? 16 : 0,
                            marginRight: msg.role === 'user' ? 0 : 16,
                          }}>
                            <p style={{
                              fontSize: T.fontSize.xs, fontWeight: 600, marginBottom: 4,
                              marginTop: 0,
                              color: msg.role === 'user' ? T.textSoft : GREEN,
                            }}>
                              {msg.role === 'user' ? 'You' : 'Teaching Coach'}
                            </p>
                            <p style={{
                              fontSize: T.fontSize.xs, color: T.text,
                              lineHeight: 1.5, whiteSpace: 'pre-wrap' as const,
                              margin: 0,
                            }}>
                              {msg.content}
                            </p>
                          </div>

                          {msg.suggestions?.map((sug) => (
                            <div key={sug.id} style={{
                              marginTop: 6, marginLeft: 8, marginRight: 8,
                              borderRadius: T.radius.md,
                              border: `1.5px solid ${
                                sug.status === 'accepted' ? GREEN
                                : sug.status === 'rejected' ? T.danger
                                : T.border
                              }`,
                              padding: '10px 12px',
                              background: sug.status === 'accepted' ? GREEN_SOFT
                                : sug.status === 'rejected' ? T.dangerSoft
                                : T.bgCard,
                              opacity: sug.status === 'rejected' ? 0.5 : 1,
                            }}>
                              <p style={{
                                fontSize: T.fontSize.xs, fontWeight: 600,
                                fontFamily: F.mono, color: GREEN,
                                margin: '0 0 4px 0', letterSpacing: 0.5,
                                textTransform: 'uppercase' as const,
                              }}>
                                {sug.section}
                              </p>
                              <p style={{
                                fontSize: T.fontSize.xs, color: T.text,
                                margin: '0 0 4px 0',
                              }}>
                                {sug.reasoning}
                              </p>

                              {/* Diff lines */}
                              {sug.currentText && (
                                <p style={{
                                  fontSize: T.fontSize.xs, fontFamily: F.mono,
                                  color: T.danger, textDecoration: 'line-through',
                                  background: T.dangerSoft,
                                  padding: '4px 8px', borderRadius: T.radius.sm,
                                  margin: '4px 0',
                                }}>
                                  {sug.currentText}
                                </p>
                              )}
                              {sug.suggestedText && (
                                <p style={{
                                  fontSize: T.fontSize.xs, fontFamily: F.mono,
                                  color: GREEN, background: GREEN_SOFT,
                                  padding: '4px 8px', borderRadius: T.radius.sm,
                                  margin: '4px 0',
                                }}>
                                  {sug.suggestedText}
                                </p>
                              )}

                              {sug.status === 'pending' ? (
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                  <button
                                    onClick={() => acceptSuggestion(msgIdx, sug.id)}
                                    style={{
                                      padding: '6px 12px', borderRadius: T.radius.sm,
                                      background: GREEN, color: '#fff',
                                      fontSize: T.fontSize.xs, fontWeight: 600,
                                      border: 'none', cursor: 'pointer',
                                    }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => rejectSuggestion(msgIdx, sug.id)}
                                    style={{
                                      padding: '6px 12px', borderRadius: T.radius.sm,
                                      background: 'transparent', color: T.textSoft,
                                      fontSize: T.fontSize.xs, fontWeight: 600,
                                      border: `1.5px solid ${T.border}`, cursor: 'pointer',
                                    }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                <span style={{
                                  fontSize: T.fontSize.xs, fontWeight: 600,
                                  marginTop: 4, display: 'inline-block',
                                  fontFamily: F.mono,
                                  color: sug.status === 'accepted' ? GREEN : T.danger,
                                }}>
                                  {sug.status === 'accepted' ? 'ACCEPTED' : 'REJECTED'}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}

                      {/* Extractor input */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="text"
                          value={extractorInput}
                          onChange={(e) => setExtractorInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendExtractor(); } }}
                          placeholder="Ask the teaching coach..."
                          disabled={isChatting}
                          style={{
                            flex: 1, background: T.bgInput,
                            border: `1px solid ${T.border}`,
                            borderRadius: T.radius.sm,
                            padding: '8px 10px',
                            fontSize: T.fontSize.xs, color: T.text,
                            fontFamily: F.body,
                            outline: 'none',
                            opacity: isChatting ? 0.5 : 1,
                          }}
                        />
                        <button
                          onClick={handleSendExtractor}
                          disabled={isChatting || !extractorInput.trim()}
                          style={{
                            padding: 8, borderRadius: T.radius.sm,
                            background: GREEN_SOFT, color: GREEN,
                            border: `1px solid ${GREEN_BORDER}`,
                            cursor: isChatting || !extractorInput.trim() ? 'not-allowed' : 'pointer',
                            opacity: isChatting || !extractorInput.trim() ? 0.5 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {isChatting ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />}
                        </button>
                      </div>

                      {/* Apply button */}
                      {acceptedCount > 0 && (
                        <button
                          onClick={handleApply}
                          style={{
                            width: '100%', padding: '10px 0',
                            borderRadius: T.radius.sm,
                            background: GREEN, color: '#fff',
                            fontWeight: 600, fontSize: T.fontSize.sm,
                            border: 'none', cursor: 'pointer',
                          }}
                        >
                          Apply {acceptedCount} Learned Rules & Reset
                        </button>
                      )}
                      {pendingCount > 0 && (
                        <button
                          onClick={() => setExtractorChat((prev) => prev.map((m) => m.suggestions ? { ...m, suggestions: m.suggestions.map((s) => ({ ...s, status: 'accepted' as const })) } : m))}
                          style={{
                            width: '100%', padding: '8px 0',
                            borderRadius: T.radius.sm,
                            background: GREEN_SOFT, color: GREEN,
                            fontWeight: 600, fontSize: T.fontSize.xs,
                            border: `1px solid ${GREEN_BORDER}`,
                            cursor: 'pointer',
                          }}
                        >
                          Accept All ({pendingCount})
                        </button>
                      )}

                      {/* Version notice */}
                      <p style={{
                        textAlign: 'center' as const, fontSize: T.fontSize.xs,
                        color: T.textMuted, margin: '4px 0',
                      }}>
                        This will create Prompt v{(activePromptConfig?.version || 0) + 1} from teach session
                      </p>

                      {/* New Session button */}
                      <button
                        onClick={handleReset}
                        style={{
                          width: '100%', padding: '12px 0',
                          marginTop: 8,
                          borderRadius: T.radius.xl,
                          background: T.bgSoft, color: T.text,
                          fontWeight: 600, fontSize: T.fontSize.sm,
                          border: `1px solid ${T.border}`,
                          cursor: 'pointer',
                        }}
                      >
                        New Teaching Session
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Chat input bar — only during teaching */}
            {phase === 'teaching' && (
              <div style={{
                flexShrink: 0, borderTop: `1px solid ${T.border}`,
                background: T.bgCard, padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type as the nurse..."
                    disabled={isPatientTyping}
                    autoFocus
                    style={{
                      flex: 1, background: T.bgInput,
                      border: `1px solid ${T.border}`,
                      borderRadius: T.radius.full,
                      padding: '12px 16px',
                      fontSize: T.fontSize.sm, color: T.text,
                      fontFamily: F.body,
                      outline: 'none',
                      opacity: isPatientTyping ? 0.5 : 1,
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={isPatientTyping || !chatInput.trim()}
                    style={{
                      flexShrink: 0, width: 44, height: 44,
                      borderRadius: T.radius.full,
                      background: GREEN, border: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: isPatientTyping || !chatInput.trim() ? 'not-allowed' : 'pointer',
                      opacity: isPatientTyping || !chatInput.trim() ? 0.4 : 1,
                    }}
                  >
                    {isPatientTyping
                      ? <Loader2 size={18} style={{ color: '#fff', animation: 'spin 1s linear infinite' }} />
                      : <Send size={18} style={{ color: '#fff' }} />}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Error toast */}
        {error && (
          <div style={{
            position: 'fixed', bottom: 80, left: 16, right: 16,
            background: T.danger, color: '#fff',
            borderRadius: T.radius.xl,
            padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 8,
            zIndex: 50, boxShadow: T.shadowLg,
          }}>
            <XCircle size={16} style={{ flexShrink: 0 }} />
            <p style={{ fontSize: T.fontSize.sm, flex: 1, margin: 0 }}>{error}</p>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
                fontSize: T.fontSize.xs, fontWeight: 600,
              }}
            >
              DISMISS
            </button>
          </div>
        )}
      </div>
    </ModuleShell>
  );
}
